// src/services/api.js
import axios from 'axios';

const API_CONFIG = {
  baseURL: '/api',
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' }
};

const RETRY_CONFIG = {
  maxRetries: 3,
  initialDelay: 1000,
  maxDelay: 5000,
};

const api = axios.create(API_CONFIG);

let lastActivityTimestamp = Date.now();const ACTIVITY_TIMEOUT = 10 * 60 * 1000;

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const cleanParams = (params) => {
  const cleaned = {};
  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value) && value.length > 0) cleaned[key] = value.join(',');
    else if (typeof value === 'string' && value.trim() !== '') cleaned[key] = value.trim();
    else if (typeof value === 'number' || typeof value === 'boolean') cleaned[key] = value;
  });
  return cleaned;
};

const shouldRetry = (error) => (
  error?.response?.status === 500 ||
  error?.response?.data?.code === '57014' ||
  error?.response?.status === 504 ||
  error?.code === 'ECONNABORTED' ||
  error?.message?.includes('Network Error')
);

const calculateBackoff = (attempt, initialDelay, maxDelay) => {
  const backoffDelay = initialDelay * Math.pow(2, attempt);
  const jitter = Math.random() * 1000;
  return Math.min(backoffDelay + jitter, maxDelay);
};

const ping = async () => {
  try {
    const response = await api.head('/ping');
    return response.status === 200;
  } catch (error) {
    console.log('Ping failed, server might be cold starting...');
    return false;
  }
};

const makeRequest = async (requestFn, config = {}) => {
  const { maxRetries, initialDelay, maxDelay } = { ...RETRY_CONFIG, ...config };
  let attempt = 0;
  while (attempt < maxRetries) {
    try {
      const timeSinceLastActivity = Date.now() - lastActivityTimestamp;
      if (timeSinceLastActivity > ACTIVITY_TIMEOUT) await ping();
      const response = await requestFn();
      lastActivityTimestamp = Date.now();
      return response;
    } catch (error) {
      attempt++;
      if (!shouldRetry(error) || attempt === maxRetries) throw error;
      const backoffDelay = calculateBackoff(attempt - 1, initialDelay, maxDelay);
      console.log(`Request failed, retrying in ${Math.round(backoffDelay/1000)}s... (Attempt ${attempt}/${maxRetries})`);
      if (error?.response?.status === 500) await ping().catch(() => {});
      await delay(backoffDelay);
    }
  }
};

export const fetchData = async (params, options = {}) => {
  const { signal, onRetry } = options;
  const cleanedParams = cleanParams(params);
  return makeRequest(
    async () => {
      try {
        const response = await api.get('/movies', {
          params: cleanedParams,
          signal,
          timeout: API_CONFIG.timeout,
        });
        return response;
      } catch (error) {
        if (onRetry) onRetry();
        throw error;
      }
    },
    options
  );
};

export const fetchUpcoming = async (params, options = {}) => {
  const cleanedParams = cleanParams(params);
  return makeRequest(
    () => api.get('/upcoming', { params: cleanedParams, signal: options.signal }),
    options
  );
};

export const fetchMovieDetails = async (id, options = {}) => {
  return makeRequest(
    async () => {
      try {
        const response = await api.get(`/movies/${id}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching movie details:', {
          status: error.response?.status,
          data: error.response?.data,
          message: error.message
        });
        throw error;
      }
    },
    options
  );
};

export const fetchSuggestions = async (params, options = {}) => {
  const cleanedParams = cleanParams(params);
  return makeRequest(
    () => api.get('/suggestions', { params: cleanedParams, signal: options.signal }),
    options
  );
};

api.interceptors.response.use(
  response => response,
  error => {
    console.error('API Error:', {
      endpoint: error.config?.url,
      status: error.response?.status,
      message: error.message,
    });
    throw error;
  }
);

export default api;
